"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { repostsAPI } from "../../services/api";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import { handleApiError } from "../../services/api";
import { isArabicText } from "../../utils/textDirection";

const RepostModal = ({ isOpen, onClose, post, onRepostSuccess }) => {
  const { t } = useTranslation();
  const { user: currentUser, getUserType } = useAuth();
  const [responseText, setResponseText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  if (!isOpen || !post) return null;

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await repostsAPI.repostPost(post.id, {
        response_text: responseText.trim() || null,
      });

      if (response?.data) {
        showSuccessToast(t("postFeed.repost.success", "Post reposted successfully!"));
        if (onRepostSuccess) {
          onRepostSuccess(response.data);
        }
        setResponseText("");
        onClose();
      }
    } catch (error) {
      handleApiError(error);
      showErrorToast(t("postFeed.repost.error", "Failed to repost. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setResponseText("");
      onClose();
    }
  };

  // Format content to handle HTML (like line breaks from Shift+Enter)
  const formatContent = (content) => {
    if (!content) return "";

    const isArabic = isArabicText(content);

    // Check if content contains HTML tags
    const hasHTML = /<[^>]+>/.test(content);

    if (hasHTML) {
      // Render HTML content using dangerouslySetInnerHTML
      // HTML content is rendered as-is since it already contains proper formatting
      return (
        <div
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // Fallback to plain text rendering for non-HTML content
    return content.split("\n").map((line, index) => (
      <div key={index}>{line || <br />}</div>
    ));
  };

  // Get display name from first_name and last_name
  const getDisplayName = (user) => {
    if (!user) return "Unknown";
    
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    if (user.first_name) {
      return user.first_name;
    }
    
    return user.name || user.username || "Unknown";
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {t("postFeed.repost.title", "Repost")}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              color: "#6B7280",
              padding: "0",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px" }}>
          {/* Current User Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <img
              src={
                currentUser?.profile_image_url ||
                getUserPlaceholderImage(currentUser)
              }
              alt={currentUser?.name || "You"}
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                objectFit: "cover",
                border: `3px solid ${userTypeColor}`,
              }}
              onError={(e) => {
                e.target.src = getUserPlaceholderImage(currentUser);
              }}
            />
            <div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#111827",
                }}
              >
                {getDisplayName(currentUser) || "You"}
              </div>
            </div>
          </div>

          {/* Response Text Input */}
          <textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder={t("postFeed.repost.placeholder", "Add a comment to your repost (optional)")}
            style={{
              width: "100%",
              minHeight: "120px",
              padding: "12px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: '"Red Hat Display", sans-serif',
              resize: "vertical",
              marginBottom: "20px",
            }}
            maxLength={500}
          />
          <div
            style={{
              fontSize: "12px",
              color: "#6B7280",
              textAlign: "right",
              marginBottom: "20px",
            }}
          >
            {responseText.length}/500
          </div>

          {/* Original Post Preview */}
          <div
            style={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              padding: "16px",
              backgroundColor: "#F9FAFB",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <img
                src={
                  post.author?.profile_image_url ||
                  post.author?.avatar ||
                  getUserPlaceholderImage(post.author)
                }
                alt={post.author?.name || "Author"}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(post.author);
                }}
              />
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#111827",
                  }}
                >
                  {getDisplayName(post.author) || "Unknown"}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6B7280",
                  }}
                >
                  {post.timestamp || "Recently"}
                </div>
              </div>
            </div>
            {post.content && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  lineHeight: "1.5",
                  marginBottom: "8px",
                }}
              >
                {formatContent(post.content)}
              </div>
            )}
            {post.image && (
              <img
                src={post.image}
                alt="Post content"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  marginTop: "8px",
                }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            padding: "20px",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              padding: "10px 20px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              background: "#FFFFFF",
              color: "#374151",
              fontSize: "14px",
              fontWeight: 500,
              cursor: isSubmitting ? "not-allowed" : "pointer",
            }}
          >
            {t("postFeed.repost.cancel", "Cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "8px",
              background: userTypeColor,
              color: "#FFFFFF",
              fontSize: "14px",
              fontWeight: 500,
              cursor: isSubmitting ? "wait" : "pointer",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            {isSubmitting
              ? t("postFeed.repost.submitting", "Reposting...")
              : t("postFeed.repost.submit", "Repost")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RepostModal;
